import { createElement, forwardRef, useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  children?: ReactNode;
  /** Angular `[indeterminate]`. */
  indeterminate?: boolean;
  /** Classes on the `mat-mdc-checkbox` host (Angular `class=`). */
  hostClassName?: string;
}

/** `<mat-checkbox>`: MDC DOM and class names (`mat-mdc-checkbox-checked`, `-disabled`) so the ported styles apply. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { children, indeterminate = false, hostClassName = '', className = '', ...rest },
  forwardedRef,
) {
  const inner = useRef<HTMLInputElement | null>(null);
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const checked = rest.checked ?? false;
  const host = [
    'mat-mdc-checkbox',
    'mat-accent',
    checked && 'mat-mdc-checkbox-checked',
    rest.disabled && 'mat-mdc-checkbox-disabled',
    hostClassName,
  ]
    .filter(Boolean)
    .join(' ');

  // Same DOM as MatCheckbox (touch target, native control, ripple, background, focus ripple), on the `mat-checkbox`
  // host element so legacy element selectors keep matching.
  return createElement(
    'mat-checkbox',
    { className: host },
    <div className="mdc-form-field">
      <div
        className={`mdc-checkbox${checked || indeterminate ? ' mdc-checkbox--selected' : ''}${rest.disabled ? ' mdc-checkbox--disabled' : ''}`}
      >
        <div className="mat-mdc-checkbox-touch-target"></div>
        <input
          {...rest}
          id={inputId}
          ref={(element) => {
            inner.current = element;
            if (typeof forwardedRef === 'function') forwardedRef(element);
            else if (forwardedRef) forwardedRef.current = element;
          }}
          type="checkbox"
          className={`mdc-checkbox__native-control ${className}`.trim()}
          data-indeterminate={indeterminate}
        />
        <div className="mdc-checkbox__ripple"></div>
        <div className="mdc-checkbox__background">
          <svg className="mdc-checkbox__checkmark" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path className="mdc-checkbox__checkmark-path" fill="none" d="M1.73,12.91 8.1,19.28 22.79,4.59" />
          </svg>
          <div className="mdc-checkbox__mixedmark"></div>
        </div>
        <div className="mat-ripple mat-mdc-checkbox-ripple mat-mdc-focus-indicator"></div>
      </div>
      <label className="mdc-label" htmlFor={inputId}>
        {children}
      </label>
    </div>,
  );
});
